import { useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import { Button, Dropdown, Tag, ToastContainer } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { matchKeywordGroupsList } from "../../graphql/queries/listKeywordGroups";
import "./productManagement.scss";

const AddKeywordGroupModal = ({
  isOpen,
  onClose,
  onSave,
  attachedKeywordGroupIds,
  editingGroup,
}) => {
  const [selectedOutletGroup, setSelectedOutletGroup] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [modalToast, setModalToast] = useState([]);
  const [showAllKeywords, setShowAllKeywords] = useState(false);

  const MAX_VISIBLE = 20;

  const { data, loading, error } = useQuery(matchKeywordGroupsList, {
    variables: { type: "OUTLET" },
    fetchPolicy: "network-only",
    skip: !isOpen,
  });

  const outletOptions = useMemo(() => {
    const attachedIds = new Set(attachedKeywordGroupIds);
    // If editing, allow the current group's ID to appear in the dropdown
    const editingId = editingGroup?.groupId;

    return (data?.listKeywordGroups || [])
      .filter((group) => !attachedIds.has(group.id) || group.id === editingId)
      .map((group) => ({ ...group, displayText: group.name }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [attachedKeywordGroupIds, data, editingGroup]);

  useEffect(() => {
    if (isOpen) {
      if (editingGroup) {
        // Pre-populate when editing an existing keyword group card
        const matchingOutlet = outletOptions.find(
          (opt) => opt.id === editingGroup.groupId
        );
        setSelectedOutletGroup(matchingOutlet ? [matchingOutlet] : []);
        setKeywords([...(editingGroup.keywords || [])]);
      } else {
        setSelectedOutletGroup([]);
        setKeywords([]);
      }
      setNewKeyword("");
      setModalToast([]);
      setShowAllKeywords(false);
    }
  }, [isOpen, editingGroup, outletOptions]);

  const addKeywords = useCallback(
    (newKeywords) => {
      const toAdd = newKeywords.map((k) => k.trim()).filter((k) => k.length > 0);

      if (toAdd.length === 0) return;

      const duplicates = toAdd.filter((k) => keywords.includes(k));
      if (duplicates.length > 0) {
        const unique = toAdd.filter((k) => !keywords.includes(k));
        if (unique.length > 0) {
          setKeywords((prev) => [...prev, ...unique]);
        }
        setModalToast([
          {
            text: `${duplicates.length} duplicate keyword(s) skipped`,
            type: "error",
            id: "dup-keyword",
          },
        ]);
        return;
      }

      setKeywords((prev) => [...prev, ...toAdd]);
      setModalToast([]);
    },
    [keywords]
  );

  const handleAddKeyword = useCallback(() => {
    const parts = newKeyword.split(",");
    addKeywords(parts);
    setNewKeyword("");
  }, [newKeyword, addKeywords]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        handleAddKeyword();
      }
    },
    [handleAddKeyword]
  );

  const handleRemoveKeyword = useCallback((keyword) => {
    setKeywords((prev) => prev.filter((k) => k !== keyword));
  }, []);

  const handleImportKeywords = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.txt";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const imported = text
          .split(/[\n,;]+/)
          .map((k) => k.trim())
          .filter((k) => k.length > 0);
        const unique = [...new Set([...keywords, ...imported])];
        setKeywords(unique);
        setModalToast([
          {
            text: `${imported.length} keywords imported`,
            type: "success",
            id: "import-success",
          },
        ]);
      } catch {
        setModalToast([
          { text: "Failed to read file", type: "error", id: "import-error" },
        ]);
      }
    };
    input.click();
  }, [keywords]);

  const handleExportKeywords = useCallback(() => {
    const groupName =
      selectedOutletGroup && selectedOutletGroup.length > 0
        ? selectedOutletGroup[0].name
        : "keywords";
    const blob = new Blob([keywords.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${groupName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [keywords, selectedOutletGroup]);

  const handleClose = useCallback(() => {
    setSelectedOutletGroup([]);
    setKeywords([]);
    setNewKeyword("");
    setModalToast([]);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(() => {
    if (!selectedOutletGroup || selectedOutletGroup.length === 0) {
      setModalToast([
        {
          text: "Please select a keyword group",
          type: "error",
          id: "group-error",
        },
      ]);
      return;
    }
    if (keywords.length === 0) {
      setModalToast([
        {
          text: "At least one keyword is required",
          type: "error",
          id: "kw-error",
        },
      ]);
      return;
    }

    const group = selectedOutletGroup[0];
    onSave({
      groupId: group.id,
      groupName: group.name,
      keywords,
    });
    handleClose();
  }, [selectedOutletGroup, keywords, onSave, handleClose]);

  if (!isOpen) return null;

  const visibleKeywords = showAllKeywords
    ? keywords
    : keywords.slice(0, MAX_VISIBLE);
  const hasMore = keywords.length > MAX_VISIBLE;

  return (
    <Dialog
      className="add-keyword-group-modal"
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <h3 style={{ margin: 0 }}>
              {editingGroup ? "Edit Keyword Group" : "Add Keyword Group"}
            </h3>
          </Grid>
          <Grid item>
            <CloseIcon
              onClick={handleClose}
              className="cursor-pointer"
              data-testid="add-keyword-group-modal-close"
            />
          </Grid>
        </Grid>
        <ToastContainer
          data={modalToast}
          removeToast={() => setModalToast([])}
          showActionIcon
        />
      </DialogTitle>
      <DialogContent>
        {/* Select Existing Keyword Group */}
        <div className="select-existing-keyword-group-field">
          <Dropdown
            childList={outletOptions}
            selection="single"
            selectedValue={selectedOutletGroup}
            setSelectedValue={setSelectedOutletGroup}
            placeholder={
              loading ? "Loading keyword groups..." : "Select keyword group"
            }
            size="medium"
            disabled={Boolean(error)}
            label="Select Existing Keywords"
          />
          {error && (
            <p className="select-existing-keyword-group-error" role="alert">
              Could not load existing keyword groups.
            </p>
          )}
        </div>

        {/* Add Keywords Section */}
        <div className="keyword-input-section">
          <div className="keyword-input-row">
            <TextField
              className="keyword-input"
              size="small"
              label="Add keywords"
              placeholder="Enter keyword and press Enter or use comma"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={handleKeyDown}
              data-testid="keyword-text-input"
            />
            <div className="keyword-actions">
              <Button
                text="Export keywords"
                variant="secondary"
                size="small"
                onClick={handleExportKeywords}
                disabled={keywords.length === 0}
              />
              <Button
                text="Import keywords"
                variant="secondary"
                size="small"
                onClick={handleImportKeywords}
              />
            </div>
          </div>

          {keywords.length > 0 && (
            <div className="current-keywords-section">
              <div className="current-keywords-label">
                Current keywords ({keywords.length})
              </div>
              <div
                className={`keywords-tags-wrap ${!showAllKeywords ? "collapsed" : ""}`}
              >
                {visibleKeywords.map((keyword, index) => (
                  <Tag
                    key={`${keyword}-${index}`}
                    text={keyword}
                    color="Gray-100"
                    isCopyable={false}
                    isRemovable={true}
                    onRemove={() => handleRemoveKeyword(keyword)}
                    size="small"
                  />
                ))}
              </div>
              {hasMore && (
                <Button
                  text={
                    showAllKeywords ? "Show less" : `Show all (${keywords.length})`
                  }
                  variant="tertiary"
                  size="small"
                  onClick={() => setShowAllKeywords(!showAllKeywords)}
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          onClick={handleClose}
          text="Cancel"
          size="small"
        />
        <Button
          variant="primary"
          onClick={handleSave}
          text="Save"
          size="small"
          disabled={
            !selectedOutletGroup ||
            selectedOutletGroup.length === 0 ||
            keywords.length === 0
          }
        />
      </DialogActions>
    </Dialog>
  );
};

AddKeywordGroupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  attachedKeywordGroupIds: PropTypes.arrayOf(PropTypes.string),
  editingGroup: PropTypes.shape({
    groupId: PropTypes.string,
    groupName: PropTypes.string,
    keywords: PropTypes.arrayOf(PropTypes.string),
  }),
};

AddKeywordGroupModal.defaultProps = {
  attachedKeywordGroupIds: [],
  editingGroup: null,
};

export default AddKeywordGroupModal;
